var m = require('mithril');
var heading = require('../components/heading');
var header = require('../layout/components/header');
var loaders = require('../layout/components/loaders');
var key = require('../libs/keybindings');
var modal = require('../layout/components/modal');
var equipmentModel = require('../equipment/models/equipment');
var serviceModel = require('../service/models/service');
var addRoomClassModal = require('../locations/addRoomClassModal');
const addServiceClassModal = require('../service/addServiceClassModal');

var state = {
  equipmentQuery: '',
  serviceQuery: '',
  zoneQuery: ''
};

var roomClassTree = { categories: [] };
var roomClassTreePromise = null;
var lastOntologyRoute = null;

function loadRoomClassTree(force) {
  if (!force && roomClassTree.categories && roomClassTree.categories.length) {
    return Promise.resolve(roomClassTree);
  }
  if (roomClassTreePromise) return roomClassTreePromise;
  roomClassTreePromise = loaders.requestWithLoader({
    loader: 'main',
    url: '/api/v1/ontology/classtree?type=room',
    method: 'GET',
    background: true
  })
  .then(tree => {
    roomClassTree = tree || { categories: [] };
    return roomClassTree;
  })
  .catch(error => {
    console.error('Error loading room classes:', error);
    roomClassTree = { categories: [] };
    return roomClassTree;
  })
  .finally(() => {
    roomClassTreePromise = null;
    m.redraw();
  });
  return roomClassTreePromise;
}

function filterNodes(nodes, query) {
  var trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return nodes;
  }

  return nodes.map(function(node) {
    return filterNode(node, trimmed);
  }).filter(Boolean);
}

function filterNode(node, query) {
  var children = Array.isArray(node.children) ? node.children.map(function(child) {
    return filterNode(child, query);
  }).filter(Boolean) : [];
  var nameMatches = node.name && node.name.toLowerCase().includes(query);

  if (nameMatches || children.length) {
    return Object.assign({}, node, {children: children});
  }

  return null;
}

function highlightName(name, query) {
  if (!query) {
    return "" + name;
  }

  var lowerName = (name || '').toLowerCase();
  var lowerQuery = query.toLowerCase();
  var idx = lowerName.indexOf(lowerQuery);

  if (idx === -1) {
    return "" + name;
  }

  return [
    name.slice(0, idx),
    m('span.bg-light-yellow.dark-gray.b', name.slice(idx, idx + query.length)),
    name.slice(idx + query.length)
  ];
}

var oninit = function() {
  header.vm.activeTab('Ontology');
  document.title = 'nimrod | Ontology';
  lastOntologyRoute = m.route.get();
  loadOntologyClassTrees().catch(() => {});
  m.redraw();
};

function loadOntologyClassTrees() {
  return Promise.all([
    equipmentModel.requestClassTree().then(m.redraw).catch(() => {}),
    serviceModel.requestClassTree().then(m.redraw).catch(() => {}),
    loadRoomClassTree(true).catch(() => {})
  ]);
}

function onupdate() {
  var currentRoute = m.route.get();
  if (currentRoute !== '/ontology') {
    return;
  }
  if (currentRoute === lastOntologyRoute) {
    return;
  }
  lastOntologyRoute = currentRoute;
  loadOntologyClassTrees().catch(() => {});
}

function sortByName(a, b) {
  return a.name.localeCompare(b.name);
}

function deleteClass(item, mainTree) {
  var className = item && item.name ? item.name : '';
  if (!className) return;

  var confirmDelete = function () {
    var deletePromise;
    if (mainTree === 'Equipment') {
      deletePromise = equipmentModel.deleteClass(className);
    } else if (mainTree === 'Service') {
      deletePromise = serviceModel.deleteClass(className);
    }

    if (!deletePromise) return;

    loaders.main.start();
    m.redraw();

    deletePromise
      .then(() => {
        loaders.main.stop();
        modal.vm.close();
        m.redraw();
      })
      .catch(error => {
        loaders.main.stop();
        var message = (error && (error.error || error.message)) ? (error.error || error.message) : 'Unable to delete class';
        modal.vm.body = function () {
          return m('div', [
            m('p', 'Are you sure you want to delete this class?'),
            m('p.red', message)
          ]);
        };
        m.redraw();
      });
  };

  key.unbind('enter');
  key.setScope('modal');
  key('enter', 'modal', confirmDelete);
  modal.vm.title(`Delete ${className}?`);
  modal.vm.body = function () {
    return m('div', [
      m('p', 'Are you sure you want to delete this class?')
    ]);
  };
  modal.vm.buttons([
    {
      label: "Cancel",
      class: "dib mr2",
      func: function () {
        key.unbind('enter');
        modal.vm.close();
      }
    },
    {
      label: "Delete",
      class: "dib red",
      func: confirmDelete
    }
  ]);
  modal.vm.open();
}

var view = function() {
  var ctrl = this;
  var equipmentCategories = Object.keys(equipmentModel.equipmentClassTree.categories)
    .filter(c => equipmentModel.equipmentClassTree.categories[c].name !== 'Overview')
    .map(nodeKey => {
      return equipmentModel.equipmentClassTree.categories[nodeKey];
    })
    .sort(sortByName);
  var zoneCategory = equipmentCategories.find(node => node && node.name === 'Zone') || { name: 'Zone', children: [] };
  equipmentCategories = equipmentCategories.filter(node => node && node.name !== 'Zone');

  var serviceCategories = Object.keys(serviceModel.measurableClassTree.categories)
    .filter(c => serviceModel.measurableClassTree.categories[c].name !== 'Overview')
    .map(nodeKey => {
      return serviceModel.measurableClassTree.categories[nodeKey];
    })
    .sort(sortByName);

  var equipmentQuery = state.equipmentQuery.trim();
  var serviceQuery = state.serviceQuery.trim();
  var zoneQuery = state.zoneQuery.trim();
  var equipmentNodes = filterNodes(equipmentCategories, equipmentQuery);
  var serviceNodes = filterNodes(serviceCategories, serviceQuery);
  var zoneNodes = filterNodes([zoneCategory], zoneQuery);
  var roomCategories = roomClassTree && roomClassTree.categories ? roomClassTree.categories : [];
  var roomRoot = roomCategories.find(node => node && node.name === 'Room') || { name: 'Room', children: [] };
  var locationNodes = [
    { name: 'Site', children: [] },
    { name: 'Building', children: [] },
    { name: 'Level', children: [] },
    roomRoot
  ];

  return m('div.mh3', [
    m(heading, {label:'Ontology', class:'f-subheadline mb3 pb1 display'}),
    m('div.mv3.bw1.pv1.f3.bb', 'Equipment'),
    m('div.mb2.ml3', [
      m('input.input-reset.ba.b--black-20.pa2.w-100', {
        type: 'search',
        placeholder: 'Search equipment classes',
        value: state.equipmentQuery,
        oninput: function(e) {
          state.equipmentQuery = e.target.value;
        }
      })
    ]),
    m('ul', { class: 'ml3 pl0' }, equipmentNodes.length ? equipmentNodes.map(node => {
      return nodeItem(ctrl, node, 'Equipment', {forceOpen: Boolean(equipmentQuery), highlightQuery: equipmentQuery});
    }) : [
      m('li.i.gray', 'No matching equipment classes')
    ]),
    m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm.w3', {
      onclick: function () {
        m.route.set(`/ontology/new?parent=Equipment`);
      }
    },[
      m('i.ion-plus.blue')
    ]),
    m('div.mv3.bw1.pv1.f3.bb', 'Zones'),
    m('div.mb2.ml3', [
      m('input.input-reset.ba.b--black-20.pa2.w-100', {
        type: 'search',
        placeholder: 'Search zone classes',
        value: state.zoneQuery,
        oninput: function(e) {
          state.zoneQuery = e.target.value;
        }
      })
    ]),
    m('ul', { class: 'ml3 pl0' }, zoneNodes.length ? zoneNodes.map(node => {
      return nodeItem(ctrl, node, 'Equipment', {forceOpen: Boolean(zoneQuery), highlightQuery: zoneQuery});
    }) : [
      m('li.i.gray', 'No matching zone classes')
    ]),
    m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm.w3', {
      onclick: function () {
        m.route.set(`/ontology/new?parent=Zone`);
      }
    },[
      m('i.ion-plus.blue')
    ]),
    m('div.mv3.bw1.pv1.f3.bb', 'Services'),
    m('div.mb2.ml3', [
      m('input.input-reset.ba.b--black-20.pa2.w-100', {
        type: 'search',
        placeholder: 'Search service classes',
        value: state.serviceQuery,
        oninput: function(e) {
          state.serviceQuery = e.target.value;
        }
      })
    ]),
    m('ul', { class: 'ml3 pl0' }, serviceNodes.length ? serviceNodes.map(node => {
      return nodeItem(ctrl, node, 'Service', {forceOpen: Boolean(serviceQuery), highlightQuery: serviceQuery});
    }) : [
      m('li.i.gray', 'No matching service classes')
    ]),
    m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm.w3', {
      onclick: function () {
        addServiceClassModal.editDialogue(undefined, {name: 'Service'}, {submitCallback: serviceModel.requestClassTree})
      }
    },[
      m('i.ion-plus.blue')
    ]),
    m('div.mv3.bw1.pv1.f3.bb', 'Locations'),
    m('ul', { class: 'ml3 pl0' }, locationNodes.map(node => {
      var isRoomClass = node && node.name === 'Room';
      return locationNodeItem(node, { forceOpen: true, isRoomClass: isRoomClass });
    })),
  ]);
};

function nodeItem(ctrl, item, mainTree, options) {
  var opts = options || {};
  var hasChildren = Array.isArray(item['children']) && item['children'].length > 0;
  var canDelete = false;
  var canEdit = true;
  if (mainTree === 'Equipment') {
    canDelete = item && item.name && item.name !== 'Zone';
    canEdit = item && item.name && item.name !== 'Zone' && item.name !== 'Equipment';
  } else if (mainTree === 'Service') {
    canDelete = Boolean(item && item.name);
  }
  return m('li', {
    style:{'list-style':'none'}, 
    class: 'mv2', 
    onclick: function(e) {
      e.stopPropagation();
      if(e.srcElement.nodeName === 'I'){
        if(e.srcElement.classList.contains('ion-arrow-right-b')){
          e.srcElement.classList.remove('ion-arrow-right-b');
          e.srcElement.classList.add('ion-arrow-down-b');
          e.srcElement.parentElement.parentElement.childNodes.forEach(element => {
            element.classList.toggle("dn");
          });
        }else if(e.srcElement.classList.contains('ion-arrow-down-b')){
          e.srcElement.classList.remove('ion-arrow-down-b');
          e.srcElement.classList.add('ion-arrow-right-b');
          e.srcElement.parentElement.parentElement.childNodes.forEach(element => {
            element.classList.toggle("dn");
          });
        }
      }else if(e.srcElement.children.length > 0 && e.srcElement.children[0].classList.contains('ion-arrow-right-b')){
        e.srcElement.children[0].classList.remove('ion-arrow-right-b');
        e.srcElement.children[0].classList.add('ion-arrow-down-b');
        this.childNodes.forEach(element => {
          element.classList.toggle("dn");
        });
      }else if(e.srcElement.children.length > 0 && e.srcElement.children[0].classList.contains('ion-arrow-down-b')){
        e.srcElement.children[0].classList.remove('ion-arrow-down-b');
        e.srcElement.children[0].classList.add('ion-arrow-right-b');
        this.childNodes.forEach(element => {
          element.classList.toggle("dn");
        });
      }      
    }
  }, [
    m('div.flex.items-center.ba.bw1.b--black-20.br2.pa2.w-100', [
      m('i', { class: `icon fr mr2 ${hasChildren ? `${opts.forceOpen ? 'ion-arrow-down-b' : 'ion-arrow-right-b'} blue` : 'ion-minus-round gray'}`}),
      m('div.flex.items-center.flex-auto',[
        highlightName(item.name, opts.highlightQuery),
        m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function () {
            if (mainTree == 'Equipment') {              
              m.route.set(`/ontology/new?parent=${item.name}`);
            }
            if (mainTree == 'Service') {
              addServiceClassModal.editDialogue(undefined, item, {submitCallback: serviceModel.requestClassTree});
            }
          }
        },[
          m('i.ion-plus.blue')
        ]),
        canEdit ? m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function () {
            if (mainTree == 'Equipment') {
              m.route.set(`/ontology/${item.name}`);              
            }
            if (mainTree == 'Service') {
              addServiceClassModal.editDialogue(item, undefined, {submitCallback: serviceModel.requestClassTree});
            }
          }
        },[
          m('i.ion-compose.blue')
        ]) : null,
        canDelete ? m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function (e) {
            e.stopPropagation();
            deleteClass(item, mainTree);
          }
        },[
          m('i.ion-ios-trash.red')
        ]) : null
      ])
    ]),
    function(){
      if (typeof item  === 'object' && !Array.isArray(item) && Object.keys(item).length > 0 && item['children']) {
        return item['children'].sort(sortByName).map(children => {
          return m('ul', {class: opts.forceOpen ? 'ml3 pl0' : 'dn ml3 pl0'}, [
            nodeItem(ctrl, children, mainTree, options)
          ])
        })
      }
    }()
  ])
}

function locationNodeItem(item, options) {
  var opts = options || {};
  var hasChildren = Array.isArray(item['children']) && item['children'].length > 0;
  var canAddChild = Boolean(opts.isRoomClass);
  var canEditClass = Boolean(opts.isRoomClass) && item && item.name && item.name !== 'Room';
  return m('li', {
    style:{'list-style':'none'}, 
    class: 'mv2', 
    onclick: function(e) {
      e.stopPropagation();
      if (e.srcElement.nodeName === 'I') {
        if (e.srcElement.classList.contains('ion-arrow-right-b')) {
          e.srcElement.classList.remove('ion-arrow-right-b');
          e.srcElement.classList.add('ion-arrow-down-b');
          e.srcElement.parentElement.parentElement.childNodes.forEach(element => {
            element.classList.toggle("dn");
          });
        } else if (e.srcElement.classList.contains('ion-arrow-down-b')) {
          e.srcElement.classList.remove('ion-arrow-down-b');
          e.srcElement.classList.add('ion-arrow-right-b');
          e.srcElement.parentElement.parentElement.childNodes.forEach(element => {
            element.classList.toggle("dn");
          });
        }
      } else if (e.srcElement.children.length > 0 && e.srcElement.children[0].classList.contains('ion-arrow-right-b')) {
        e.srcElement.children[0].classList.remove('ion-arrow-right-b');
        e.srcElement.children[0].classList.add('ion-arrow-down-b');
        this.childNodes.forEach(element => {
          element.classList.toggle("dn");
        });
      } else if (e.srcElement.children.length > 0 && e.srcElement.children[0].classList.contains('ion-arrow-down-b')) {
        e.srcElement.children[0].classList.remove('ion-arrow-down-b');
        e.srcElement.children[0].classList.add('ion-arrow-right-b');
        this.childNodes.forEach(element => {
          element.classList.toggle("dn");
        });
      }
    }
  }, [
    m('div.flex.items-center.ba.bw1.b--black-20.br2.pa2.w-100', [
      m('i', { class: `icon fr mr2 ${hasChildren ? `${opts.forceOpen ? 'ion-arrow-down-b' : 'ion-arrow-right-b'} blue` : 'ion-minus-round gray'}`}),
      m('div.flex.items-center.flex-auto', [
        item.name || '',
        canAddChild ? m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function (e) {
            e.stopPropagation();
            addRoomClassModal.addDialogue(item, { submitCallback: () => loadRoomClassTree(true) });
          }
        }, [
          m('i.ion-plus.blue')
        ]) : null,
        canEditClass ? m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function (e) {
            e.stopPropagation();
            addRoomClassModal.editDialogue(item, { submitCallback: () => loadRoomClassTree(true) });
          }
        }, [
          m('i.ion-compose.blue')
        ]) : null,
        canEditClass ? m('div.ml3.ba.bw1.ph3.pv1.tc.v-btm', {
          onclick: function (e) {
            e.stopPropagation();
            addRoomClassModal.deleteDialogue(item, { submitCallback: () => loadRoomClassTree(true) });
          }
        }, [
          m('i.ion-ios-trash.red')
        ]) : null
      ])
    ]),
    function(){
      if (typeof item  === 'object' && !Array.isArray(item) && Object.keys(item).length > 0 && item['children']) {
        return item['children'].sort(sortByName).map(children => {
          return m('ul', {class: opts.forceOpen ? 'ml3 pl0' : 'dn ml3 pl0'}, [
            locationNodeItem(children, { forceOpen: opts.forceOpen, isRoomClass: opts.isRoomClass })
          ])
        })
      }
    }()
  ])
}


module.exports = {oninit: oninit, onupdate: onupdate, view: view}
